// src/components/player/tiles/Tile4TrendVelocityPitcher.js
// Tile 4: Trend Velocity Analysis (PITCHER VERSION)
// Shows performance trends across rolling 5-game windows
// Filtered to league's scoring categories

import React from 'react';
import { dynastyTheme } from '../../../services/colorService';
import { TrendingUp } from 'lucide-react';

const Tile4TrendVelocityPitcher = ({ data, loading, error }) => {
  // Tile wrapper
  const Tile = ({ children }) => (
    <div className={`${dynastyTheme.components.card.base} h-full`}>
      {children}
    </div>
  );

  // Format stat name
  const formatStatName = (fieldName) => {
    const labels = {
      'wins': 'W',
      'saves': 'SV',
      'holds': 'HLD',
      'era': 'ERA',
      'whip': 'WHIP',
      'strikeouts_pitched': 'K',
      'quality_starts': 'QS',
      'k_per_9': 'K/9',
      'bb_per_9': 'BB/9'
    };
    return labels[fieldName] || fieldName.toUpperCase();
  };

  // Format trend value
  const formatTrendValue = (fieldName, value) => {
    if (value === null || value === undefined) return '-';
    
    // Rate stats
    if (['era', 'whip', 'k_per_9', 'bb_per_9'].includes(fieldName)) {
      return value.toFixed(2);
    }
    
    // Counting stats
    return Math.round(value).toString();
  };

  // Get trend color
  const getTrendColor = (direction) => {
    if (direction === 'improving') return dynastyTheme.classes.text.success;
    if (direction === 'declining') return dynastyTheme.classes.text.error;
    return dynastyTheme.classes.text.neutral;
  };

  // Get overall trend color
  const getOverallTrendColor = (overallTrend) => {
    if (overallTrend?.includes('🔥')) return dynastyTheme.classes.text.success;
    if (overallTrend?.includes('📈')) return dynastyTheme.classes.text.success;
    if (overallTrend?.includes('❄️')) return dynastyTheme.classes.text.error;
    return dynastyTheme.classes.text.neutral;
  };

  const hasData = data?.trends && Object.keys(data.trends).length > 0;
  const role = data?.role || 'pitcher';

  return (
    <Tile>
      <div className="pt-1 px-4 pb-3 flex flex-col tile-scroll" style={{ height: '182px', overflow: 'auto' }}>
        <h4 className={`text-base font-semibold ${dynastyTheme.classes.text.primary} mb-3 flex items-center`}>
          <TrendingUp className="w-4 h-4 mr-2" />
          📈 Trend Analysis
        </h4>
        
        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className={`text-xs ${dynastyTheme.classes.text.neutralLight}`}>
              Loading trends...
            </div>
          </div>
        ) : error ? (
          <div className="flex-1 flex flex-col justify-center">
            <div className={`text-xs ${dynastyTheme.classes.text.error}`}>
              {error}
            </div>
          </div>
        ) : hasData ? (
          <div className="flex-1 flex flex-col">
            {/* Overall Momentum */}
            <div className="text-center mb-3 pb-3 border-b border-gray-700">
              <div className={`text-base ${getOverallTrendColor(data.overall_trend)}`}>
                {data.overall_trend}
              </div>
            </div>

            {/* Trends List */}
            <div className="flex-1 overflow-y-auto space-y-1.5">
              {Object.entries(data.trends).map(([stat, trend]) => {
                const mostRecent = trend.values[0];

                return (
                  <div key={stat} className="text-sm">
                    {/* Stat Name & Current Value */}
                    <div className="flex justify-between items-center mb-0.5">
                      <span className={dynastyTheme.classes.text.neutralLight}>
                        {formatStatName(stat)}
                      </span>
                      <span className={dynastyTheme.classes.text.white}>
                        {formatTrendValue(stat, mostRecent)}
                      </span>
                    </div>

                    {/* Trend Indicator & Change */}
                    <div className="flex justify-between items-center">
                      <span className={getTrendColor(trend.direction)}>
                        {trend.symbol} {trend.direction}
                      </span>
                      <span className={getTrendColor(trend.direction)}>
                        {trend.change_pct > 0 ? '+' : ''}{trend.change_pct}%
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Footer */}
            {data.windows && (
              <div className={`text-xs ${dynastyTheme.classes.text.neutral} pt-2 mt-2 border-t border-gray-700`}>
                <div>{data.windows.length} rolling 5-game windows</div>
                <div className="opacity-75">Role: {role}</div>
              </div>
            )}
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className={`text-xs ${dynastyTheme.classes.text.neutralLight} text-center`}>
              {/* Cluster-D.3: starters use per-start windows (3 starts -> trend computable). */}
              Need 3+ starts (starters) or 10+ games (relievers) for trend analysis
            </div>
          </div>
        )}
      </div>
    </Tile>
  );
};

export default Tile4TrendVelocityPitcher;
